/**
 * DarkModeCallsheetStyles.js
 * 
 * This file contains theme-aware styles for the Callsheet components
 * to improve the dark mode experience.
 */

// Function to get theme-aware styles for different components
const getCallsheetStyles = (theme) => {
  const { palette, shadows } = theme;
  const { mode } = palette;
  
  // Removed unused createAlpha helper

  return {
    // Main container styles
    container: {
      backgroundColor: mode === 'dark' ? palette.background.default : palette.background.paper,
      color: mode === 'dark' ? palette.text.primary : palette.text.primary,
    },
    
    // Paper component styles
    paper: {
      backgroundColor: mode === 'dark' ? 'rgba(30, 30, 35, 0.95)' : palette.background.paper,
      boxShadow: mode === 'dark' 
        ? '0 8px 32px rgba(0,0,0,0.5)' 
        : '0 4px 20px rgba(0,0,0,0.1)',
      borderRadius: '8px',
      borderColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
      transition: 'all 0.3s ease',
      '&:hover': {
        boxShadow: mode === 'dark'
          ? '0 12px 40px rgba(0,0,0,0.6)'
          : '0 8px 28px rgba(0,0,0,0.15)',
      },
    },
    
    // Paper component used in tables
    tablePaper: mode === 'dark' ? {
      backgroundColor: 'rgba(20, 20, 25, 0.8)',
      boxShadow: '0 4px 20px rgba(0,0,0,0.4)',
    } : {},
    
    // Section headers
    sectionHeader: {
      borderBottom: `2px solid ${mode === 'dark' ? palette.primary.dark : palette.primary.light}`,
      color: mode === 'dark' ? palette.primary.light : palette.primary.main,
      padding: '16px 0',
      marginBottom: '24px',
      position: 'relative',
      '&::after': {
        content: '""',
        position: 'absolute',
        bottom: '-2px',
        left: 0,
        width: '60px',
        height: '2px',
        backgroundColor: mode === 'dark' ? palette.primary.main : palette.primary.dark,
      },
    },
    
    // Tab styles
    tabs: {
      borderBottom: `1px solid ${mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
      minHeight: '48px',
      '& .MuiTabs-indicator': {
        height: '3px',
        borderRadius: '3px 3px 0 0',
        backgroundColor: palette.primary.main,
      },
    },
    
    tab: {
      textTransform: 'none',
      fontWeight: 500,
      fontSize: '0.95rem',
      minHeight: '48px',
      padding: '6px 16px',
      color: mode === 'dark' ? palette.text.secondary : palette.text.primary,
      '&.Mui-selected': {
        color: mode === 'dark' ? palette.primary.light : palette.primary.main,
        fontWeight: 600,
      },
      '&:hover': {
        color: mode === 'dark' ? palette.primary.light : palette.primary.main,
        opacity: 0.8,
      },
    },

    // Cards
    card: {
      backgroundColor: mode === 'dark' ? 'rgba(30, 30, 35, 0.8)' : palette.background.paper,
      boxShadow: mode === 'dark' 
        ? '0 8px 32px rgba(0,0,0,0.4)' 
        : '0 4px 16px rgba(0,0,0,0.08)',
      borderRadius: 1,
      borderColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
      padding: '24px',
      transition: 'all 0.3s ease',
      '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: mode === 'dark'
          ? '0 12px 40px rgba(0,0,0,0.5)'
          : '0 8px 24px rgba(0,0,0,0.12)'
      },
    },
    
    // Panel content
    panelContent: {
      padding: '24px 0',
      '& .MuiGrid-container': {
        gap: '24px',
      },
    },
    
    // Form fields
    formField: {
      marginBottom: '16px',
      '& .MuiInputLabel-root': {
        color: mode === 'dark' ? palette.text.secondary : palette.text.primary,
        fontSize: '0.95rem',
      },
      '& .MuiOutlinedInput-root': {
        borderRadius: '8px',
        backgroundColor: mode === 'dark' 
          ? 'rgba(0, 0, 0, 0.2)' 
          : 'rgba(0, 0, 0, 0.02)',
        '&:hover': {
          backgroundColor: mode === 'dark' 
            ? 'rgba(0, 0, 0, 0.3)' 
            : 'rgba(0, 0, 0, 0.04)',
        },
        '&.Mui-focused': {
          backgroundColor: mode === 'dark' 
            ? 'rgba(0, 0, 0, 0.4)' 
            : 'rgba(0, 0, 0, 0.06)',
        },
      },
      '& .MuiInputBase-input': {
        fontSize: '0.95rem',
      },
    },
    
    // Table styles
    table: mode === 'dark' ? {
      backgroundColor: 'rgba(20, 20, 25, 0.8)',
      '& .MuiPaper-root': {
        backgroundColor: 'rgba(20, 20, 25, 0.8)',
      },
      '& .MuiTableCell-head': {
        backgroundColor: mode === 'dark' ? 'rgba(30, 30, 40, 0.8)' : 'rgba(240, 240, 250, 0.8)',
        color: mode === 'dark' ? palette.primary.light : palette.primary.dark,
        fontWeight: 'bold',
      },
      '& .MuiTableRow-root:nth-of-type(even)': {
        backgroundColor: mode === 'dark' ? 'rgba(40, 40, 50, 0.3)' : 'rgba(245, 245, 255, 0.3)',
      },
      '& .MuiTableCell-root': {
        borderBottomColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
        color: mode === 'dark' ? palette.text.primary : palette.text.primary,
      },
    } : {
      '& .MuiTableCell-head': {
        backgroundColor: 'rgba(240, 240, 250, 0.8)',
        color: palette.primary.dark,
        fontWeight: 'bold',
      },
      '& .MuiTableRow-root:nth-of-type(even)': {
        backgroundColor: 'rgba(245, 245, 255, 0.3)',
      },
      '& .MuiTableCell-root': {
        borderBottomColor: 'rgba(0, 0, 0, 0.1)',
        color: palette.text.primary,
      },
    },
    
    // Shooting schedule table
    scheduleTable: mode === 'dark' ? {
      '& .MuiTableRow-root': {
        '&:nth-of-type(odd)': {
          backgroundColor: 'rgba(30, 30, 35, 0.3)',
        },
        '&:nth-of-type(even)': {
          backgroundColor: 'rgba(30, 30, 35, 0.1)',
        },
        '&:hover': {
          backgroundColor: 'rgba(30, 30, 35, 0.5)',
        },
      },
      '& .MuiTableCell-root': {
        borderColor: 'rgba(255, 255, 255, 0.1)',
        color: palette.text.primary,
      },
      '& .MuiTextField-root': {
        '& .MuiInputBase-root': {
          backgroundColor: 'rgba(30, 30, 35, 0.6)',
          color: palette.text.primary,
          '&:hover': {
            backgroundColor: 'rgba(30, 30, 35, 0.8)',
          },
          '&.Mui-focused': {
            backgroundColor: 'rgba(30, 30, 35, 0.9)',
          },
        },
        '& .MuiInputBase-input': {
          color: palette.text.primary,
          '&::placeholder': {
            color: 'rgba(255, 255, 255, 0.5)',
            opacity: 1,
          },
        },
        '& .MuiFormHelperText-root': {
          color: 'rgba(255, 255, 255, 0.7)',
        },
        '& .MuiOutlinedInput-notchedOutline': {
          borderColor: 'rgba(255, 255, 255, 0.1)',
        },
        '&:hover .MuiOutlinedInput-notchedOutline': {
          borderColor: palette.primary.light,
        },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
          borderColor: palette.primary.main,
          borderWidth: 1,
        },
      },
      '& .MuiFormControl-root': {
        '& .MuiInputBase-root': {
          backgroundColor: 'rgba(30, 30, 35, 0.6)',
          color: palette.text.primary,
          '&:hover': {
            backgroundColor: 'rgba(30, 30, 35, 0.8)',
          },
          '&.Mui-focused': {
            backgroundColor: 'rgba(30, 30, 35, 0.9)',
          },
        },
        '& .MuiSelect-select': {
          color: palette.text.primary,
          '&::placeholder': {
            color: 'rgba(255, 255, 255, 0.5)',
            opacity: 1,
          },
        },
        '& .MuiOutlinedInput-notchedOutline': {
          borderColor: 'rgba(255, 255, 255, 0.1)',
        },
        '&:hover .MuiOutlinedInput-notchedOutline': {
          borderColor: palette.primary.light,
        },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
          borderColor: palette.primary.main,
          borderWidth: 1,
        },
      },
    } : {},
    
    // Schedule table container
    scheduleTableContainer: mode === 'dark' ? {
      backgroundColor: 'rgba(20, 20, 25, 0.8)',
      '& .MuiPaper-root': {
        backgroundColor: 'rgba(20, 20, 25, 0.8)',
      }
    } : {},
    
    // Multiline text field for descriptions
    scheduleDescriptionField: mode === 'dark' ? {
      '& .MuiInputBase-root': {
        backgroundColor: 'rgba(30, 30, 35, 0.6)',
        color: palette.text.primary,
        '&:hover': {
          backgroundColor: 'rgba(30, 30, 35, 0.8)',
        },
        '&.Mui-focused': {
          backgroundColor: 'rgba(30, 30, 35, 0.9)',
        },
      },
      '& .MuiInputBase-input': {
        color: palette.text.primary,
        '&::placeholder': {
          color: 'rgba(255, 255, 255, 0.5)',
          opacity: 1,
        },
      },
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: 'rgba(255, 255, 255, 0.1)',
      },
      '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: palette.primary.light,
      },
      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: palette.primary.main,
        borderWidth: 1,
      },
    } : {},
    
    // Table header backgrounds for different sections
    crewTableHeader: {
      backgroundColor: mode === 'dark' ? `rgba(${parseInt(palette.secondary.dark.slice(1, 3), 16)}, ${parseInt(palette.secondary.dark.slice(3, 5), 16)}, ${parseInt(palette.secondary.dark.slice(5, 7), 16)}, 0.2)` : '#f3e5f5',
    },
    
    scheduleTableHeader: mode === 'dark' ? {
      backgroundColor: 'rgba(46, 125, 50, 0.3)',
      '& .MuiTableCell-root': {
        color: '#ffffff',
        fontWeight: 'bold',
        textShadow: '0px 1px 2px rgba(0, 0, 0, 0.5)',
      }
    } : {},
    
    talentTableHeader: {
      backgroundColor: mode === 'dark' ? `rgba(${parseInt(palette.error.dark.slice(1, 3), 16)}, ${parseInt(palette.error.dark.slice(3, 5), 16)}, ${parseInt(palette.error.dark.slice(5, 7), 16)}, 0.2)` : '#ffebee',
    },
    
    // Shooting schedule text fields
    scheduleTextField: mode === 'dark' ? {
      '& .MuiInputBase-root': {
        backgroundColor: `rgba(${parseInt(palette.background.paper.slice(1, 3), 16)}, ${parseInt(palette.background.paper.slice(3, 5), 16)}, ${parseInt(palette.background.paper.slice(5, 7), 16)}, 0.7)`,
        color: palette.text.primary,
      },
      '& .MuiInputBase-input': {
        color: palette.text.primary,
      },
      '& .MuiFormHelperText-root': {
        color: palette.text.secondary,
      },
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: `rgba(${parseInt(palette.divider.slice(1, 3), 16)}, ${parseInt(palette.divider.slice(3, 5), 16)}, ${parseInt(palette.divider.slice(5, 7), 16)}, 0.7)`,
      },
      '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: palette.primary.light,
      },
      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: palette.primary.main,
      },
    } : {},
    
    // Warning section (used in TalentCalls)
    warningSection: mode === 'dark' ? {
      backgroundColor: 'rgba(30, 30, 35, 0.8)',
      borderTop: '4px solid #f44336',
      '& .MuiTypography-root': {
        color: palette.text.primary,
      },
      '& .MuiBorderBottom': {
        borderColor: 'rgba(255, 255, 255, 0.1)',
      },
    } : {},

    // Cast selection area
    castSelectionArea: mode === 'dark' ? {
      border: '1px dashed rgba(244, 67, 54, 0.5)',
      backgroundColor: 'rgba(30, 30, 35, 0.4)',
    } : {},

    // Cast member card
    castMemberCard: mode === 'dark' ? {
      backgroundColor: 'rgba(30, 30, 35, 0.6)',
      borderColor: 'rgba(255, 255, 255, 0.1)',
      '&:hover': {
        backgroundColor: 'rgba(244, 67, 54, 0.2)',
        borderColor: 'rgba(244, 67, 54, 0.5)',
      },
    } : {},

    // Removed duplicate talentTableHeader key

    // Error box
    errorBox: mode === 'dark' ? {
      backgroundColor: 'rgba(30, 30, 35, 0.4)',
      color: '#f44336',
    } : {},
    
    // Tabs
    mainTabs: {
      ...(mode === 'dark' ? {
        backgroundColor: 'rgba(25, 32, 72, 0.9)',
      } : {}),
      '& .MuiTab-root': {
        color: '#fff',
        opacity: mode === 'dark' ? 0.8 : 0.9,
      },
      '& .Mui-selected': {
        color: '#fff',
        opacity: 1,
      },
      '& .MuiTabs-indicator': {
        backgroundColor: '#fff',
      },
    },
    
    // Section tabs
    sectionTabs: {
      ...(mode === 'dark' ? {
        backgroundColor: 'rgba(30, 40, 80, 0.4)',
      } : {
        backgroundColor: palette.primary.main,
      }),
      borderRadius: '4px',
      '& .MuiTab-root': {
        color: '#fff',
        opacity: mode === 'dark' ? 0.8 : 0.9,
        '&:hover': {
          color: '#fff',
          opacity: 1,
        },
      },
      '& .Mui-selected': {
        color: '#fff',
        opacity: 1,
      },
      '& .MuiTabs-indicator': {
        backgroundColor: '#fff',
      },
    },
    
    // Tab container
    tabContainer: {
      backgroundColor: mode === 'dark' ? 'rgba(25, 25, 30, 0.4)' : 'rgba(245, 245, 250, 0.7)',
      borderRadius: 1,
      padding: 1,
    },
    
    // Removed duplicate button key (primary button styles defined below)
    
    // Icons
    iconButton: {
      backgroundColor: mode === 'dark' ? 'rgba(66, 66, 77, 0.1)' : 'transparent',
      color: mode === 'dark' ? palette.text.primary : palette.text.primary,
      '&:hover': {
        backgroundColor: mode === 'dark' ? 'rgba(66, 66, 77, 0.2)' : 'rgba(66, 66, 77, 0.1)',
      }
    },
    
    // Dialogs
    dialog: {
      backgroundColor: mode === 'dark' ? 'rgba(30, 30, 35, 0.9)' : palette.background.paper,
      backgroundImage: 'none',
    },
    
    // Dividers
    divider: {
      borderColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
    },
    
    // Section papers with colored borders
    primarySection: {
      borderTop: `4px solid ${palette.primary.main}`,
      backgroundColor: mode === 'dark' ? 'rgba(30, 30, 35, 0.8)' : 'linear-gradient(to right, #f5f7fa, #ffffff)',
      boxShadow: mode === 'dark' ? '0 4px 20px rgba(0,0,0,0.4)' : shadows[1],
    },
    
    secondarySection: {
      borderTop: `4px solid ${palette.secondary.main}`,
      backgroundColor: mode === 'dark' ? 'rgba(30, 30, 35, 0.8)' : palette.background.paper,
      boxShadow: mode === 'dark' ? '0 4px 20px rgba(0,0,0,0.4)' : shadows[1],
    },
    
    infoSection: {
      borderTop: `4px solid ${palette.info.main}`,
      backgroundColor: mode === 'dark' ? 'rgba(30, 30, 35, 0.8)' : palette.background.paper,
      boxShadow: mode === 'dark' ? '0 4px 20px rgba(0,0,0,0.4)' : shadows[1],
    },
    
    successSection: {
      borderTop: `4px solid ${palette.success.main}`,
      backgroundColor: mode === 'dark' ? 'rgba(30, 30, 35, 0.8)' : palette.background.paper,
      boxShadow: mode === 'dark' ? '0 4px 20px rgba(0,0,0,0.4)' : shadows[1],
    },
    
    // Removed duplicate warningSection key (definition exists above)
    
    errorSection: {
      borderTop: `4px solid ${palette.error.main}`,
      backgroundColor: mode === 'dark' ? 'rgba(30, 30, 35, 0.8)' : 'linear-gradient(to right, #fff5f8, #ffffff)',
      boxShadow: mode === 'dark' ? '0 4px 20px rgba(0,0,0,0.4)' : shadows[1],
    },
    
    // Form controls
    formControl: {
      '& .MuiInputBase-root': {
        backgroundColor: mode === 'dark' ? 'rgba(40, 40, 50, 0.3)' : palette.background.paper,
      },
      '& .MuiInputLabel-root': {
        color: mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.7)',
      },
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)',
      },
    },
    
    // Button styles (authoritative definition)
    button: {
      borderRadius: '8px',
      padding: '8px 16px',
      fontSize: '0.9rem',
      fontWeight: 500,
      textTransform: 'none',
      transition: 'all 0.2s ease',
      '&.MuiButton-contained': {
        boxShadow: mode === 'dark'
          ? '0 4px 12px rgba(0,0,0,0.3)'
          : '0 2px 8px rgba(0,0,0,0.1)',
        '&:hover': {
          transform: 'translateY(-1px)',
          boxShadow: mode === 'dark'
            ? '0 6px 16px rgba(0,0,0,0.4)'
            : '0 4px 12px rgba(0,0,0,0.15)',
        },
      },
      '&.MuiButton-outlined': {
        borderWidth: '1.5px',
        '&:hover': {
          borderWidth: '1.5px',
          backgroundColor: mode === 'dark'
            ? 'rgba(255, 255, 255, 0.05)'
            : 'rgba(0, 0, 0, 0.03)',
        },
      },
    },
  };
};

export default getCallsheetStyles;
